import { unsealData } from 'iron-session';
import { sealData } from 'iron-session';

import { appSettings } from '@/settings/app-settings';
import {
  DASHBOARD_BANNER_DISMISSED_LS_KEY,
  DASHBOARD_SURVEY_SKIPPED_LS_KEY,
} from '@/ux/pages/BackOffice/components/widget-dashboard/dashboard-constants';

import { updateAppSession } from '../apis/app-api';
import { ROLE } from '../constants/app-constants';
import { configureGQL, loadGQL } from '../queries/gql-loader';
import { Authentication } from '../store/global/app-context-types';
import { LoginData, ProfilesData } from '../store/global/app-data-type';
import { isServer } from './helpers';
import {
  cacheSessionDataInContext,
  clearSessionCacheInContext,
  getCachedSessionDataFromContext,
} from './request-context';

/**
 * Check if the current host is a custom domain by comparing with CLIENT_APP_URL
 * @param currentHost - The host to check (e.g., window.location.host or request host)
 * @returns true if it's a custom domain, false if it's the main app domain
 */
export function isCustomDomain(currentHost: string): boolean {
  if (!currentHost) return false;

  // Extract the host from CLIENT_APP_URL
  const clientAppUrl = appSettings.api.CLIENT_APP_URL || '';
  if (!clientAppUrl) {
    // If CLIENT_APP_URL is not set, treat localhost as not custom
    return currentHost !== 'localhost' && currentHost !== 'localhost:3000';
  }

  let mainAppHost = '';
  try {
    const url = new URL(clientAppUrl);
    mainAppHost = url.host;
  } catch (_error) {
    console.error('Invalid CLIENT_APP_URL:', clientAppUrl);
    // If we can't parse CLIENT_APP_URL, be conservative and return false
    return false;
  }

  // Normalize the hosts for comparison (remove port if default)
  const normalizeHost = (host: string) => {
    // Remove default ports
    return host.replace(/:80$/, '').replace(/:443$/, '');
  };

  const normalizedCurrent = normalizeHost(currentHost);
  const normalizedMain = normalizeHost(mainAppHost);

  // Special handling for localhost development
  if (normalizedCurrent === 'localhost' || normalizedCurrent === 'localhost:3000') {
    // In development, localhost is never considered a custom domain
    return false;
  }

  // Compare normalized hosts
  // If they're different, it's a custom domain
  return normalizedCurrent !== normalizedMain;
}

export function setAuthentication(loginData: LoginData, profilesData: ProfilesData): Authentication | null {
  if (loginData?.jwt) {
    // Check if logout is in progress - don't login during logout
    if (!isServer()) {
      const logoutTransition = localStorage.getItem('logoutTransition');
      const logoutTransitionTime = localStorage.getItem('logoutTransitionTime');

      if (logoutTransition === 'true' && logoutTransitionTime) {
        const transitionAge = Date.now() - parseInt(logoutTransitionTime);
        if (transitionAge < 3000) {
          // Don't login during logout (3 second window)
          console.warn('Login blocked - logout in progress');
          return null;
        }
      }
    }

    // CRITICAL: Clear all caches before setting new authentication
    clearSessionCache();

    // Clear any guest tokens from localStorage and set login transition flag
    if (!isServer()) {
      // Clear any logout transition flags if they exist
      localStorage.removeItem('logoutTransition');
      localStorage.removeItem('logoutTransitionTime');
      localStorage.removeItem('guestJwt');
      localStorage.removeItem('tempUserId');
      // Set login transition flag to prevent race conditions
      localStorage.setItem('loginTransition', 'true');
      localStorage.setItem('loginTransitionTime', Date.now().toString());
    }

    // Reset token refresh manager to clear any pending refresh attempts
    import('./token-refresh-manager').then(({ tokenRefreshManager }) => {
      tokenRefreshManager.reset();
    });

    // Dispatch auth change event for UI components
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('auth-change', {
          detail: { type: 'login' },
        }),
      );

      // Clear login transition flag after successful authentication setup
      setTimeout(() => {
        localStorage.removeItem('loginTransition');
        localStorage.removeItem('loginTransitionTime');
      }, 2000);
    }

    const profiles = profilesData.getProfiles ?? [];
    const profile = profiles.find((object: any) => object._type == 'master') || profiles[0];
    setLocalStorage(loginData, profile?.id, profile?.DisplayPic);
    configureGQL(appSettings.api.GRAPHQL, authTool);

    const jwtData = parseJwtData(loginData.jwt);
    return {
      loggedIn: true,
      profileId: loginData.id,
      jwt: loginData.jwt,
      role: jwtData.role,
      userId: jwtData.userId || loginData.id,
      refreshToken: loginData.refreshToken,
      defaultProfile: profile?.id,
      profiles: loginData.profiles,
      handle: jwtData?.handle,
      masterProfile: profile || null,
      profileData: {
        id: loginData.id,
        fullName: loginData.fullName,
        email: loginData.email,
        isEmailVerified: loginData.isEmailVerified,
        isPhoneVerified: loginData.isPhoneVerified,
        phone: loginData.phone,
        handle: loginData.Handle,
      },
    };
  }

  return null;
}

export async function getAuthentication(fresh = false): Promise<Authentication | null> {
  const loginData: LoginData | null = getLocalStorage();

  // Use client-safe method to get session data
  const cookieData = isServer()
    ? await getCookieStorageFromServer()
    : await getClientSessionData(fresh ? { cache: 'no-store' } : undefined);

  if (cookieData !== null) {
    return {
      loggedIn: cookieData?.role == ROLE.GUEST ? false : true,
      profileId: cookieData?.role == ROLE.GUEST ? undefined : loginData?.id,
      jwt: cookieData.jwt,
      role: cookieData.role,
      refreshToken: cookieData.refreshToken,
      userId: cookieData.userId,
      profileData: {
        id: loginData?.id,
        fullName: cookieData?.name,
        email: cookieData.email,
        isEmailVerified: cookieData?.isEmailVerified,
        isPhoneVerified: loginData?.isPhoneVerified,
        phone: cookieData?.phone,
        isAdmin: loginData?.isAdmin,
        handle: loginData?.Handle,
        isAccountDeleteRequested: parseJwtData(cookieData.jwt)?.isAccountDeleteRequested,
        accountDeleteRequestedAt: parseJwtData(cookieData.jwt)?.accountDeleteRequestedAt,
      },
    };
  }
  return null;
}

export async function clearAuthentication(): Promise<void> {
  if (!isServer()) {
    // Check if login is in progress - don't logout during login
    const loginTransition = localStorage.getItem('loginTransition');
    const loginTransitionTime = localStorage.getItem('loginTransitionTime');

    if (loginTransition === 'true' && loginTransitionTime) {
      const transitionAge = Date.now() - parseInt(loginTransitionTime);
      if (transitionAge < 3000) {
        // Don't logout during login (3 second window)
        console.warn('Logout blocked - login in progress');
        return;
      }
    }

    // Clear any login transition flags if they exist
    localStorage.removeItem('loginTransition');
    localStorage.removeItem('loginTransitionTime');

    // Set logout transition flag to prevent race conditions during logout
    localStorage.setItem('logoutTransition', 'true');
    localStorage.setItem('logoutTransitionTime', Date.now().toString());

    // Clear session cache first
    clearSessionCache();

    localStorage.removeItem('defaultProfile');
    // localStorage.removeItem('jwt');
    // localStorage.removeItem('refreshToken');
    localStorage.removeItem('email');
    localStorage.removeItem('isEmailVerified');
    localStorage.removeItem('isPhoneVerified');
    localStorage.removeItem('loginType');
    localStorage.removeItem('CheckoutId');
    localStorage.removeItem('lastPage');
    localStorage.removeItem('lastPageIndex');
    localStorage.removeItem('VendorCustomDomain');
    localStorage.removeItem('guestJwt');
    localStorage.removeItem('tempUserId');
    // Clear login transition flags
    localStorage.removeItem('loginTransition');
    localStorage.removeItem('loginTransitionTime');
    // localStorage.removeItem('profiles');

    // Clear all brainwave onboarding data and dashboard survey-skipped flags on logout
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (
        key &&
        (key.startsWith('brainwave-onboarding-') ||
          key.startsWith(DASHBOARD_SURVEY_SKIPPED_LS_KEY) ||
          key.startsWith(DASHBOARD_BANNER_DISMISSED_LS_KEY))
      ) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));

    // Clear all user content contexts on logout
    try {
      // Import dynamically to avoid circular dependencies
      import('@/ux/features/content/user-space/utils/userContentContext').then((module) => {
        module.clearAllUserContentContexts();
      });
    } catch (error) {
      console.error('Failed to clear user content contexts:', error);
    }

    // Notify server to clear session cookies
    try {
      await fetch('/api/auth/clear', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'X-Logout-Request': 'true',
        },
      });
    } catch (error) {
      console.error('Failed to clear server authentication:', error);
    }

    // Reset token refresh manager
    import('./token-refresh-manager').then(({ tokenRefreshManager }) => {
      tokenRefreshManager.reset();
    });

    // Dispatch logout event for UI components
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('auth-change', {
          detail: { type: 'logout' },
        }),
      );

      // Clear logout transition flag after logout process completes
      setTimeout(() => {
        localStorage.removeItem('logoutTransition');
        localStorage.removeItem('logoutTransitionTime');
      }, 3000);
    }
  }
}

function setLocalStorage(
  loginData: LoginData,
  defaultProfile: string | undefined,
  _DisplayPic: string | undefined,
) {
  try {
    if (loginData?.jwt) {
      localStorage.setItem('defaultProfile', defaultProfile || '');
      // localStorage.setItem('jwt', loginData.jwt);
      // localStorage.setItem('refreshToken', loginData?.refreshToken || '');
      // localStorage.setItem('email', loginData?.email || '');
      localStorage.setItem('isEmailVerified', Boolean(loginData?.isEmailVerified).toString());
      localStorage.setItem('isPhoneVerified', Boolean(loginData?.isPhoneVerified).toString());
      localStorage.setItem('loginType', 'defaultLogin');
      localStorage.removeItem('lastPage');
      localStorage.removeItem('lastPageIndex');
      // localStorage.setItem('isAdmin', 'false');
      // localStorage.setItem('profiles', JSON.stringify(loginData?.profiles));
      // localStorage.removeItem('role');
    }
  } catch (error) {
    console.log(error);
  }
}

function getLocalStorage(): LoginData | null {
  try {
    // const jwt = localStorage.getItem('jwt');
    // const role = localStorage.getItem('role');
    // if (jwt) {
    //&& getJwtData().role != ROLE.GUEST
    const loginData: LoginData = {
      id: localStorage.getItem('defaultProfile') || undefined,
      // jwt,
      // refreshToken: localStorage.getItem('refreshToken') || undefined,
      // email: localStorage.getItem('email') || undefined,
      isEmailVerified: localStorage.getItem('isEmailVerified') === 'true',
      isPhoneVerified: localStorage.getItem('isPhoneVerified') === 'true',
      // isAdmin: localStorage.getItem('isAdmin') === 'true',
    };
    return loginData;
    // }
  } catch (error) {
    console.log(error);
  }

  return null;
}

/**
 * @deprecated This function should not be used directly. Use getClientSessionData() for client or getCookieStorageFromServer() for server.
 * This wrapper now redirects to the appropriate method based on the environment.
 */
export async function getCookieStorage(): Promise<any | null> {
  // Redirect to appropriate method based on environment
  if (typeof window !== 'undefined') {
    console.warn(
      'getCookieStorage() is being used on client-side. ' +
        'This call has been redirected to getClientSessionData(). ' +
        'Please update your code to call getClientSessionData() directly.',
    );
    return getClientSessionData();
  }

  // Server-side: use the server method
  return getCookieStorageFromServer();
}

export async function getCookieStorageFromServer(cookiesData?: any): Promise<any | null> {
  // SECURITY FIX: Always require explicit cookies - never fall back to shared cache
  if (!cookiesData) {
    // If no cookies provided, fetch from Next.js cookies() - this is request-scoped and safe
    try {
      const { cookies } = await import('next/headers');
      const cookieStore = await cookies();
      const allCookies = cookieStore.getAll();
      cookiesData = allCookies.reduce((obj: any, item: any) => {
        obj[item.name] = item.value;
        return obj;
      }, {});
    } catch (error) {
      console.error('Failed to get request-scoped cookies:', error);
      return null;
    }
  }

  try {
    const userCookie: any = await unsealData((cookiesData?.user as string) || '', {
      password: process.env.IRON_SESSION_PASS || '',
    });

    const tokenCookie: any = await unsealData((cookiesData?.token as string) || '', {
      password: process.env.IRON_SESSION_PASS || '',
    });

    const refreshTokenCookie: any = await unsealData((cookiesData?.['refresh-token'] as string) || '', {
      password: process.env.IRON_SESSION_PASS || '',
    });

    const tenantCookie: any = await unsealData((cookiesData?.['tenant-info'] as string) || '', {
      password: process.env.IRON_SESSION_PASS || '',
    });

    // Clean up JWT values to ensure they're either valid or null
    const jwt = tokenCookie?.app?.jwt;
    const refreshToken = refreshTokenCookie?.app?.refreshToken;

    const loginData: any = {
      jwt: jwt && jwt !== 'null' && jwt !== 'undefined' ? jwt : null,
      refreshToken:
        refreshToken && refreshToken !== 'null' && refreshToken !== 'undefined' ? refreshToken : null,
      email: userCookie?.user?.email || '',
      role: userCookie?.user?.role || 'guest',
      phoneNumber: userCookie?.user?.phoneNumber,
      userId: userCookie?.user?.id,
      tenantId: tenantCookie?.tenant?.id,
    };

    return loginData;
  } catch (error) {
    console.log(error);
  }

  return null;
}

export function setLoginRedirect(path: string) {
  try {
    localStorage.setItem('redirectPath', path);
  } catch (error) {
    console.log(error);
  }

  return null;
}

export function clearLoginRedirect() {
  try {
    localStorage.removeItem('redirectPath');
  } catch (error) {
    console.log(error);
  }
  return null;
}

export const authTool = {
  sendAuthExpired: () => {
    // Dispatch event for UI components
    if (typeof window !== 'undefined') {
      console.log('[authTool] Sending auth-expired event');
      window.dispatchEvent(new CustomEvent('auth-expired'));

      // Schedule automatic recovery after backoff
      import('./token-refresh-manager').then(({ tokenRefreshManager }) => {
        tokenRefreshManager.scheduleRecovery();
      });
    }
  },
  refreshContextData: async (_fresh?: boolean) => {},
  clearCartData: () => {},
};

export const getJwtData = () => {
  const base64Payload =
    localStorage?.getItem('jwt')?.split('.')[1] || localStorage?.getItem('guestJwt')?.split('.')[1] || '';

  return JSON.parse(atob(base64Payload) || '{}');
};

export const parseJwtData = (jwtData: string) => {
  if (!jwtData || jwtData === 'null' || jwtData === 'undefined') {
    return {};
  }

  try {
    const parts = jwtData.split('.');
    if (parts.length !== 3) {
      return {};
    }

    const base64Payload = parts[1];
    if (!base64Payload) {
      return {};
    }

    return JSON.parse(atob(base64Payload));
  } catch (error) {
    console.error('Failed to parse JWT:', error);
    return {};
  }
};

export async function getGuestUserJwt(force = false) {
  // If force=true, skip ALL validation and proceed directly to fetching guest token
  if (force) {
    console.log('[getGuestUserJwt] ⚠️ FORCE MODE - Skipping all session validation checks');
    // Proceed directly to fetching guest token (skip all checks below)
  } else if (!isServer()) {
    // Normal mode: perform all validation checks
    const loginTransition = localStorage.getItem('loginTransition');
    const loginTransitionTime = localStorage.getItem('loginTransitionTime');

    // Get current session state (force fresh fetch to avoid stale cache)
    const sessionData = await getClientSessionData({ cache: 'no-store' });

    // Check if JWT is expired
    let isJwtExpired = false;
    if (sessionData?.jwt && sessionData.jwt !== 'null' && sessionData.jwt !== 'undefined') {
      try {
        const jwtData = parseJwtData(sessionData.jwt);
        if (jwtData?.exp) {
          const expiresIn = jwtData.exp - Math.floor(Date.now() / 1000);
          isJwtExpired = expiresIn <= 0;
        }
      } catch (e) {
        console.error('Failed to parse JWT for expiry check:', e);
      }
    }

    const hasValidUserJwt =
      sessionData?.jwt &&
      sessionData.jwt !== 'null' &&
      sessionData.jwt !== 'undefined' &&
      sessionData.role &&
      sessionData.role !== 'guest' &&
      !isJwtExpired; // Only valid if not expired

    if (loginTransition === 'true' && loginTransitionTime) {
      const transitionAge = Date.now() - parseInt(loginTransitionTime);
      if (transitionAge < 10000) {
        // During login transition, only block if we have a user JWT that would be overwritten
        if (hasValidUserJwt) {
          console.log('Blocking guest token fetch - would overwrite user JWT during login transition');
          throw new Error('User JWT present during login transition');
        }
        // Allow guest token if no JWT exists at all (needed for GraphQL)
        console.log('Login transition but no user JWT, allowing guest token for GraphQL');
      }
    }

    // Check if user has active session - block only if they have a valid, non-expired user JWT
    const hasUserSession = localStorage.getItem('loginType') === 'defaultLogin';
    if (hasUserSession && hasValidUserJwt) {
      console.log('Active user session with valid JWT detected, blocking guest token fetch');
      throw new Error('User session active with valid JWT');
    }

    // Log if we're allowing guest token because JWT is expired
    if (hasUserSession && isJwtExpired) {
      console.log('[getGuestUserJwt] Allowing guest token fetch - user session exists but JWT is expired');
    }
  }

  return new Promise((resolve: any, reject: any) => {
    loadGQL()
      .then((gql) => gql.getTenantInfoJwt())
      .then(async (resp: any) => {
        if (!isServer()) {
          localStorage.setItem('defaultProfile', parseJwtData(resp?.data?.getTenantInfo?.jwt)?.tempUserId);
          await updateAppSession({
            jwt: resp?.data?.getTenantInfo?.jwt,
            refreshToken: resp?.data?.getTenantInfo?.refreshToken,
          });
        }

        resolve(resp?.data?.getTenantInfo?.jwt, resp?.data?.getTenantInfo?.refreshToken);
      })
      .catch((err) => {
        reject(err);
      });
  });
}

export const doLogout = async () => {
  // Check if we're on a custom domain
  if (typeof window !== 'undefined') {
    const currentHost = window.location.host;

    // For custom domains, don't use Cognito logout - just redirect to login
    // This avoids the Cognito error since custom domains aren't registered logout URIs
    if (isCustomDomain(currentHost)) {
      console.log('Custom domain detected, redirecting to local /login instead of Cognito logout');

      // Use replace instead of href to avoid keeping logout in history
      window.location.replace('/login');
      return;
    }
  }

  // For the main app domain, use Cognito logout
  const requestUrl =
    `${appSettings.api.OPEN_ID_COGNITO_URL}/logout` +
    `?client_id=${appSettings.api.OPEN_ID_COGNITO_CLIENT_ID}` +
    `&logout_uri=${window.origin}/${appSettings.api.OPEN_ID_COGNITO_LOGOUT_PATH}`;

  window.location.href = requestUrl;
};

// SECURITY FIX: Removed module-level cookieData cache to prevent cross-user token leakage
// The previous implementation stored cookies in a shared module-level variable,
// which caused User A to read User B's authentication data in concurrent requests.

export let hostName = '';

/**
 * @deprecated This function is deprecated and should not be used.
 * Pass cookies directly to getCookieStorageFromServer() instead.
 * This function now does nothing and will be removed in a future version.
 */
export const setCookiesData = (_cookieData: any, host: string) => {
  // SECURITY: This function is now a no-op to prevent cache pollution
  // Set hostName only for backward compatibility with GraphQL calls
  if (host && !host.startsWith('http')) {
    hostName = `https://${host}`;
  } else {
    hostName = host;
  }
};

/**
 * @deprecated This function is deprecated and should not be used.
 * Use request-scoped cookies from Next.js cookies() instead.
 */
export const getCookiesData = () => {
  console.warn(
    'getCookiesData() is deprecated and returns empty object. Use request-scoped cookies instead.',
  );
  return {};
};

export async function updateServerCookie(jwt: string, refreshToken: string): Promise<any> {
  // SECURITY NOTE: Server-side cookie updates during request processing are limited.
  // This function seals the data but cannot modify HTTP response cookies mid-request.
  //
  // For proper token refresh:
  // - Client-side: Use updateAppSession() which calls /api/session
  // - Server-side: Refreshed tokens won't persist to cookies until next request
  //
  // The GraphQL client will use the refreshed tokens for retrying the current request,
  // but they won't be saved to cookies. This is acceptable because:
  // 1. The client will eventually call /api/session to persist them
  // 2. Each request fetches fresh cookies anyway

  console.log('[updateServerCookie] Note: Server-side cookie updates are not persisted mid-request');

  const parsedJwtObj = parseJwtData(jwt);
  const jwtSession = { app: { jwt } };
  const refreshTokenSession = { app: { refreshToken } };
  const tenantInfoSession = {
    tenant: {
      id: parsedJwtObj?.tenantId || '',
      logo: parsedJwtObj?.tenantLogo || '',
      name: parsedJwtObj?.companyName || '',
      website: parsedJwtObj?.companyWebsite || '',
    },
  };
  const userInfoSession = {
    user: {
      id: parsedJwtObj?.userId || '',
      name: parsedJwtObj?.name || '',
      email: parsedJwtObj?.email || '',
      role: parsedJwtObj?.role || 'guest',
      phoneNumber: parsedJwtObj?.phone_number,
    },
  };

  const jwtData: any = await sealData(jwtSession, {
    password: process.env.IRON_SESSION_PASS || '',
  });
  const refreshTokenData: any = await sealData(refreshTokenSession, {
    password: process.env.IRON_SESSION_PASS || '',
  });
  const userData: any = await sealData(userInfoSession, {
    password: process.env.IRON_SESSION_PASS || '',
  });
  const tenantData: any = await sealData(tenantInfoSession, {
    password: process.env.IRON_SESSION_PASS || '',
  });

  // Return sealed data - caller would need to write to response cookies
  // In practice, this is not used because server-side can't update cookies mid-request
  return {
    token: jwtData,
    'refresh-token': refreshTokenData,
    user: userData,
    'tenant-info': tenantData,
  };
}

// SECURITY FIX: Replaced module-level cache with request-scoped cache
// The previous implementation used module-level variables that leaked data between requests
const CACHE_DURATION = 30000; // Cache for 30 seconds - reduces API calls while staying fresh

// In-flight request tracking per-session (keyed by session/request ID)
const sessionPromises = new Map<string, Promise<any>>();

// Client-side time-based cache, layered on top of the in-flight dedup. The
// in-flight Map only collapses concurrent calls into one fetch; without this
// cache, every navigation burst that lands AFTER the previous promise
// resolves triggers a fresh /api/auth/session round-trip. Bypassed when a
// caller passes `{ cache: 'no-store' }` (used by the fresh-session paths).
// Cleared on login/logout via clearSessionCache().
let clientSessionCache: { data: any; timestamp: number } | null = null;

// Generation counter bumped on every clearSessionCache(). Each in-flight
// fetch captures the generation at start; if it changes by the time the
// fetch resolves, the result is discarded instead of repopulating the cache.
// Without this, a logout that fires while a session fetch is in flight
// would have its clear overwritten by the resolving fetch's old data.
let sessionCacheGeneration = 0;

/**
 * Get cached session data if available and fresh
 * Now uses request-scoped cache instead of module-level cache
 */
export function getCachedSessionData(): any | null {
  // Try to get from request context first (server-side)
  if (isServer()) {
    return getCachedSessionDataFromContext(CACHE_DURATION);
  }

  // Client-side: still use module-level cache (safe on client since it's per-browser)
  // This is the legacy path for backwards compatibility
  return null;
}

/**
 * Client-safe function to get session data via API
 * This should be used instead of getCookieStorage() in client components
 *
 * @param options - Optional fetch options to override defaults
 */
export async function getClientSessionData(options?: RequestInit): Promise<any | null> {
  if (typeof window === 'undefined') {
    // If called on server, use the existing server method with request-scoped caching
    const cached = getCachedSessionDataFromContext(CACHE_DURATION);
    if (cached) {
      return cached;
    }

    const data = await getCookieStorageFromServer();

    // Cache in request context if available
    if (data) {
      cacheSessionDataInContext(data);
    }

    return data;
  }

  // Client-side implementation with per-request deduplication
  // On client, module-level cache is safe since it's per-browser
  const bypassCache = options?.cache === 'no-store';
  const requestKey = `client-session-${options?.cache || 'default'}`;

  // Time-based cache: return the fresh-enough cached value synchronously
  // unless caller explicitly requested a fresh fetch.
  if (!bypassCache && clientSessionCache && Date.now() - clientSessionCache.timestamp < CACHE_DURATION) {
    return clientSessionCache.data;
  }

  // Check for in-flight request to avoid duplicate API calls
  const inflightPromise = sessionPromises.get(requestKey);
  if (inflightPromise) {
    return inflightPromise;
  }

  // Snapshot the generation BEFORE awaiting so we can detect a concurrent
  // clearSessionCache() (e.g. a logout fired while this fetch is in flight)
  // and avoid repopulating the just-cleared cache with stale data.
  const startGeneration = sessionCacheGeneration;

  // Create new request
  const promise = (async () => {
    try {
      const response = await fetch('/api/auth/session', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        cache: options?.cache,
      });

      if (!response.ok) {
        console.error('Failed to get session:', response.status);
        return null;
      }

      const data = await response.json();

      if (data.error) {
        console.error('Session error:', data.error);
        return null;
      }

      // Only populate the time-based cache if no clearSessionCache() ran
      // while this fetch was in flight. Otherwise discard so the next call
      // hits the server with the post-clear cookie state.
      if (sessionCacheGeneration === startGeneration) {
        clientSessionCache = { data, timestamp: Date.now() };
      }
      return data;
    } catch (error) {
      console.error('Failed to get client session:', error);
      return null;
    } finally {
      // Clear the promise after completion
      sessionPromises.delete(requestKey);
    }
  })();

  sessionPromises.set(requestKey, promise);
  return promise;
}

/**
 * Clear the session cache to force a fresh fetch on next call
 * SECURITY FIX: Now clears request-scoped cache instead of module-level cache
 */
export function clearSessionCache(): void {
  // Clear request context cache (server-side)
  if (isServer()) {
    clearSessionCacheInContext();
  }

  // Clear any in-flight promises
  sessionPromises.clear();

  // Clear the client time-based cache so the next call hits the server.
  // Bumping the generation also invalidates any in-flight fetch's right to
  // write back to clientSessionCache — see getClientSessionData() above.
  clientSessionCache = null;
  sessionCacheGeneration += 1;
}
